import app from 'app';
import PictureInPictureAdapter from 'c/PictureInPictureAdapter';

export default class UndoController {
  /** @namespace font.defaultFont */
  /** @namespace font.databaseIdentifier */
  constructor({ initialState = {}, bindKeys = true, size = 21 } = {}) {
    this.size = size;
    this.history = new Array(size);
    this.history[0] = Object.assign({}, initialState);
    this.index = 0;
    this.callbacks = {};
    if (bindKeys) {
      this.bindKeys();
    }
  }

  bindKeys() {
    const isMac = navigator.platform === 'MacIntel';
    const metaProp = isMac ? 'metaKey' : 'ctrlKey';
    const handlers = [
      { on: 'keydown', key: 90, run: this.undo.bind(this), [metaProp]: true },
      {
        on: 'keydown',
        key: 90,
        run: this.redo.bind(this),
        [metaProp]: true,
        shiftKey: true,
      },
    ];
    if (!isMac) {
      handlers.push({
        on: 'keydown',
        key: 89,
        run: this.redo.bind(this),
        ctrlKey: true,
      });
    }
    app.keyboardAccessibility.add(app.KEYBOARD_LAYER.UNDO, handlers);
  }

  getCurrent() {
    return this.history[this.index];
  }

  _isTheSame(historyItem) {
    // compare copies of data after stripping temp fields
    const prevHistoryItem = this.history[this.index];

    // console.diff?.(prevHistoryItem.data, historyItem.data);

    function prepareToCompare(data) {
      const rv = _.cloneDeepAndClean(data);
      _.each(rv.visualTracks, (asset) => {
        delete asset.guid;
        delete asset.realTimePreviewChecksum;
        delete asset.realTimePreviewResultData;
        _.each(asset[PictureInPictureAdapter.TRACK_PROP], (overlay) => {
          delete overlay.guid;
        });
      });
      return rv;
    }

    return _.isEqual(
      prepareToCompare(prevHistoryItem.data),
      prepareToCompare(historyItem.data)
    );
  }

  push(historyItem) {
    if (this._isTheSame(historyItem)) {
      return;
    }

    const item = Object.assign({}, historyItem);
    if (this.index === this.size - 1) {
      this.history.shift();
      this.history.push(item);
    } else {
      for (let i = this.index + 1; i < this.size; i++) {
        this.history[i] = undefined;
      }

      this.history[++this.index] = item;
    }
  }

  undo(e) {
    if (this.canUndo() && !(e && this.keyboardDisabled)) {
      const newState = this.history[--this.index];
      this.runCallbacks('undo', newState.component, newState);
    }

    return this;
  }

  redo(e) {
    if (this.canRedo() && !(e && this.keyboardDisabled)) {
      const newState = this.history[++this.index];
      this.runCallbacks('redo', newState.component, newState);
    }

    return this;
  }

  canUndo() {
    return !!this.index;
  }

  canRedo() {
    return this.index !== this.size - 1 && !!this.history[this.index + 1];
  }

  disableKeyboard() {
    this.keyboardDisabled = true;
  }

  enableKeyboard() {
    this.keyboardDisabled = false;
  }

  clear(state) {
    if (!state) {
      state = this.getCurrent();
    }
    this.history.fill(undefined);
    this.history[0] = state;
    this.index = 0;
  }

  destroy() {
    app.keyboardAccessibility.remove(app.KEYBOARD_LAYER.UNDO);
    this.history = null;
    this.callbacks = null;
  }

  runCallbacks(type, component, newState) {
    _.eachIn(this.callbacks, `${type}.${component}`, (cb) => {
      cb(newState);
    });
  }

  on(types, component, callback) {
    if (typeof callback !== 'function') {
      return;
    }

    const typeArray = types.split(' ');
    for (const type of typeArray) {
      if (!this.callbacks[type]) {
        this.callbacks[type] = {};
      }
      if (!this.callbacks[type][component]) {
        this.callbacks[type][component] = [];
      }
      this.callbacks[type][component].push(callback);
    }
  }
}
